import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { sedesAPI, companiesAPI, usersAPI, floorsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { MapPin, Plus, Trash2, Loader2, Building2, Users, Layers } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const SedesPage = () => {
  const { user } = useAuth();
  const [sedes, setSedes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sedeStats, setSedeStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', company_id: '', address: '', city: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sedesRes, companiesRes] = await Promise.all([
        sedesAPI.getAll(),
        user?.role === 'superadmin' ? companiesAPI.getAll() : Promise.resolve({ data: [] })
      ]);
      
      setSedes(sedesRes.data);
      
      // For admin, we need to get their company info
      let companiesList = companiesRes.data;
      if (user?.role === 'admin' && user?.company_id) {
        // Admin only has one company - their own
        companiesList = [{ id: user.company_id, name: 'Mi Empresa' }];
      }
      setCompanies(companiesList);
      
      // Load stats for each sede (users count, floors count)
      const stats = {};
      for (const sede of sedesRes.data) {
        try {
          const [usersRes, floorsRes] = await Promise.all([
            usersAPI.getAll({ sede_id: sede.id }),
            floorsAPI.getAll(sede.id)
          ]);
          stats[sede.id] = {
            users: usersRes.data?.length || 0,
            floors: floorsRes.data?.length || 0
          };
        } catch (e) {
          stats[sede.id] = { users: 0, floors: 0 };
        }
      }
      setSedeStats(stats);
      
      if (companiesList.length > 0) {
        setFormData(prev => ({ ...prev, company_id: companiesList[0].id }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await sedesAPI.create(formData);
      toast.success('Sede creada exitosamente');
      setDialogOpen(false);
      setFormData({ name: '', company_id: formData.company_id, address: '', city: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear sede');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta sede?')) return;
    try {
      await sedesAPI.delete(id);
      toast.success('Sede eliminada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar sede');
    }
  };

  // Get company name by ID
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Sin empresa';
  };

  // Group sedes by company for better visualization
  const sedesByCompany = sedes.reduce((acc, sede) => {
    const companyId = sede.company_id || 'sin_empresa';
    if (!acc[companyId]) {
      // For admin, use a friendly name
      let companyName = 'Sin Empresa Asignada';
      if (user?.role === 'admin') {
        companyName = 'Mi Empresa';
      } else {
        const found = companies.find(c => c.id === companyId);
        if (found) companyName = found.name;
      }
      acc[companyId] = {
        company: { id: companyId, name: companyName },
        sedes: []
      };
    }
    acc[companyId].sedes.push(sede);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Gestión de Sedes
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Administra las sedes asociadas a cada empresa
            </p>
          </div>
          <Button 
            onClick={() => setDialogOpen(true)} 
            className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
            data-testid="add-sede-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Sede
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : sedes.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay sedes registradas</p>
              <Button 
                onClick={() => setDialogOpen(true)} 
                className="mt-4 bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
              >
                Crear primera sede
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.values(sedesByCompany).map(({ company, sedes: companySedes }) => (
              <div key={company.id} className="space-y-3">
                {/* Company Header */}
                <div className="flex items-center gap-3 px-1">
                  <div className="w-8 h-8 bg-[#002FA7] rounded-sm flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg">{company.name}</h2>
                    <p className="text-xs text-slate-500">{companySedes.length} sede(s)</p>
                  </div>
                </div>
                
                {/* Sedes Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-11">
                  {companySedes.map((sede) => (
                    <Card key={sede.id} className="border border-slate-200 card-hover" data-testid={`sede-card-${sede.id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-50 rounded-sm flex items-center justify-center">
                              <MapPin className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{sede.name}</CardTitle>
                              {sede.city && <p className="text-xs text-slate-500 mt-0.5">{sede.city}</p>}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(sede.id)}
                            className="text-slate-400 hover:text-red-500"
                            data-testid={`delete-sede-${sede.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {sede.address && (
                          <p className="text-sm text-slate-600 mb-3">{sede.address}</p>
                        )}
                        
                        {/* Stats */}
                        <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Users className="w-4 h-4" />
                            <span className="text-sm font-medium">{sedeStats[sede.id]?.users || 0}</span>
                            <span className="text-xs">usuarios</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Layers className="w-4 h-4" />
                            <span className="text-sm font-medium">{sedeStats[sede.id]?.floors || 0}</span>
                            <span className="text-xs">pisos</span>
                          </div>
                        </div>
                        
                        {/* Company badge for superadmin */}
                        {user?.role === 'superadmin' && (
                          <div className="mt-3 pt-2 border-t border-slate-100">
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-700 rounded-sm text-xs font-medium border border-blue-200">
                              <Building2 className="w-3 h-3" />
                              {getCompanyName(sede.company_id)}
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nueva Sede</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {user?.role === 'superadmin' && companies.length > 0 && (
                <div className="space-y-2">
                  <Label>Empresa *</Label>
                  <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })}>
                    <SelectTrigger className="rounded-sm" data-testid="company-select">
                      <SelectValue placeholder="Seleccionar empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {c.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">La sede quedará asociada a esta empresa</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la sede *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm"
                  placeholder="Ej: Sede Central Lima"
                  data-testid="sede-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Ciudad</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="rounded-sm"
                  placeholder="Ej: Lima"
                  data-testid="sede-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="rounded-sm"
                  placeholder="Ej: Av. Javier Prado 1234"
                  data-testid="sede-address-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving || (user?.role === 'superadmin' && !formData.company_id)} data-testid="save-sede-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default SedesPage;
